import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const ADMIN_DRAWER_EXIT_DURATION_MS = 180;
const DASHBOARD_DRAWER_DURATION_MS = 180;
const REDUCED_MOTION_EXIT_DURATION_MS = 0;

interface AdminDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  backdrop?: "dim" | "transparent";
  onSubmit?: () => void;
  submitDisabled?: boolean;
  closeDisabled?: boolean;
  mobilePresentation?: "sheet" | "centered";
  desktopPresentation?: "centered" | "right-drawer";
  visualTreatment?: "default" | "operational-sheet" | "dashboard";
  onAfterClose?: () => void;
  initialFocusRef?: RefObject<HTMLElement | null>;
}

export function AdminDrawer({
  open,
  onClose,
  title,
  children,
  backdrop = "dim",
  onSubmit,
  submitDisabled = false,
  closeDisabled = false,
  mobilePresentation = "sheet",
  desktopPresentation = "centered",
  visualTreatment = "default",
  onAfterClose,
  initialFocusRef,
}: AdminDrawerProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const isOperationalSheet = visualTreatment === "operational-sheet";
  const isDashboard = visualTreatment === "dashboard";
  const isContextualSurface = desktopPresentation === "right-drawer";
  const isRightDrawer = isDesktop && isContextualSurface;
  const desktopSidebarWidth = 220;
  const useCenteredMobile = !isDesktop && mobilePresentation === "centered";
  const isMobileSheet = !isDesktop && mobilePresentation === "sheet";
  const mobilePresentationMode = isDesktop
    ? "desktop"
    : useCenteredMobile
      ? "centered"
      : "sheet";
  const exitDuration = isDashboard
    ? DASHBOARD_DRAWER_DURATION_MS
    : ADMIN_DRAWER_EXIT_DURATION_MS;
  const hasManagedContent = isContextualSurface || isDashboard;
  const initialFocusTargetRef = useRef<HTMLElement | null>(
    typeof document !== "undefined" &&
      document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  const [isMounted, setIsMounted] = useState(open);
  const [isVisible, setIsVisible] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const afterCloseRef = useRef(onAfterClose);
  const shouldNotifyAfterCloseRef = useRef(false);
  afterCloseRef.current = onAfterClose;
  const requestClose = () => {
    if (!closeDisabled) onClose();
  };
  useDialogFocus(dialogRef, requestClose, {
    enabled: isMounted,
    initialFocusRef,
    restoreTarget: isDashboard ? initialFocusTargetRef.current : undefined,
  });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      const targetTag = (e.target as HTMLElement | null)?.tagName;
      const isSelectTarget = targetTag === "SELECT";
      const isButtonTarget = targetTag === "BUTTON";
      const isTextareaTarget = targetTag === "TEXTAREA";

      if (
        e.key === "Enter" &&
        !e.defaultPrevented &&
        !e.shiftKey &&
        !isSelectTarget &&
        !isButtonTarget &&
        !isTextareaTarget &&
        !submitDisabled &&
        onSubmit
      ) {
        onSubmit();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onSubmit, submitDisabled]);

  useEffect(() => {
    if (open) {
      shouldNotifyAfterCloseRef.current = false;
      setIsMounted(true);
      const requestFrame =
        typeof window.requestAnimationFrame === "function"
          ? window.requestAnimationFrame.bind(window)
          : (cb: FrameRequestCallback) =>
              window.setTimeout(() => cb(Date.now()), 16);
      const cancelFrame =
        typeof window.cancelAnimationFrame === "function"
          ? window.cancelAnimationFrame.bind(window)
          : window.clearTimeout.bind(window);
      const frame = requestFrame(() => setIsVisible(true));
      return () => cancelFrame(frame);
    }

    if (!isMounted) return;
    setIsVisible(false);
    const timeout = setTimeout(
      () => {
        shouldNotifyAfterCloseRef.current = true;
        setIsMounted(false);
      },
      prefersReducedMotion ? REDUCED_MOTION_EXIT_DURATION_MS : exitDuration,
    );
    return () => clearTimeout(timeout);
  }, [exitDuration, isMounted, open, prefersReducedMotion]);

  useEffect(() => {
    if (isMounted || open || !shouldNotifyAfterCloseRef.current) return;
    shouldNotifyAfterCloseRef.current = false;
    afterCloseRef.current?.();
  }, [isMounted, open]);

  if (!isMounted) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      data-mobile-presentation={mobilePresentationMode}
      data-desktop-presentation={desktopPresentation}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: isDesktop ? `${desktopSidebarWidth}px` : 0,
        zIndex: 1200,
        display: "flex",
        alignItems: isRightDrawer
          ? "stretch"
          : isDesktop || useCenteredMobile
            ? "center"
            : "flex-end",
        justifyContent: isRightDrawer ? "flex-end" : "center",
        padding: isRightDrawer
          ? "max(0px, env(safe-area-inset-top)) max(0px, env(safe-area-inset-right)) max(0px, env(safe-area-inset-bottom)) 0"
          : isDesktop
            ? "max(1rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom))"
            : useCenteredMobile
              ? "max(0.75rem, env(safe-area-inset-top)) 0.75rem max(0.75rem, env(safe-area-inset-bottom))"
              : "0",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .admin-drawer-close {
          transition: background 0.16s ease, color 0.16s ease, opacity 0.16s ease;
        }
        .admin-drawer-close:hover {
          opacity: 0.82;
          background: color-mix(in srgb, var(--login-text) 10%, transparent);
          color: var(--login-text);
        }
        .admin-drawer-close:active {
          opacity: 0.7;
        }
        @media (prefers-reduced-motion: reduce) {
          .admin-drawer-backdrop,
          .admin-drawer-panel,
          .admin-drawer-close {
            transition-duration: ${REDUCED_MOTION_EXIT_DURATION_MS}ms !important;
            transform: none !important;
          }
        }
      `}</style>
      <div
        className="admin-drawer-backdrop"
        onClick={requestClose}
        style={{
          position: "absolute",
          inset: 0,
          background:
            backdrop === "dim"
              ? "var(--dashboard-overlay-backdrop)"
              : "transparent",
          transition: "opacity 0.18s ease",
          opacity: isVisible ? 1 : 0,
        }}
      />
      <div
        className="admin-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-mobile-presentation={mobilePresentationMode}
        data-desktop-presentation={desktopPresentation}
        data-visual-treatment={
          isOperationalSheet
            ? "operational-sheet"
            : isDashboard
              ? "dashboard"
              : undefined
        }
        ref={dialogRef}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: isRightDrawer
            ? "32rem"
            : isDashboard && isDesktop
              ? "46rem"
              : isDesktop
                ? "512px"
                : isMobileSheet
                  ? "none"
                  : "480px",
          height: isRightDrawer
            ? "100%"
            : isDashboard
              ? isDesktop
                ? "min(45rem, calc(100vh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))"
                : "min(78vh, calc(100vh - 5.5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))"
              : undefined,
          background: "var(--login-surface)",
          border: "1px solid var(--login-border)",
          borderBottom: isMobileSheet ? "none" : undefined,
          borderRadius: isRightDrawer
            ? "0"
            : isOperationalSheet
              ? isDesktop || useCenteredMobile
                ? "6px"
                : "8px 8px 0 0"
              : isDashboard
                ? isDesktop || useCenteredMobile
                  ? "0.75rem"
                  : "0.75rem 0.75rem 0 0"
                : isDesktop || useCenteredMobile
                  ? "1rem"
                  : "1rem 1rem 0 0",
          padding: isMobileSheet
            ? "max(1.25rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) max(1.25rem, env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))"
            : isOperationalSheet || isDashboard
              ? "1.25rem"
              : "1.5rem",
          boxSizing: "border-box",
          maxHeight: isRightDrawer
            ? "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))"
            : isDesktop
              ? "min(90vh, calc(100vh - 2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))"
              : useCenteredMobile
                ? "min(85vh, calc(100vh - 1.5rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)))"
                : "100dvh",
          overflowY: hasManagedContent ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
          gap: isOperationalSheet ? "1rem" : "1.25rem",
          opacity: isVisible ? 1 : 0,
          transform: isVisible
            ? isRightDrawer
              ? "translateX(0)"
              : "translateY(0) scale(1)"
            : isRightDrawer
              ? "translateX(28px)"
              : isDesktop || useCenteredMobile
                ? "translateY(18px) scale(0.965)"
                : "translateY(28px) scale(0.99)",
          transition: isDashboard
            ? `opacity ${DASHBOARD_DRAWER_DURATION_MS}ms ease, transform ${DASHBOARD_DRAWER_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
            : `opacity 0.2s ease, transform ${ADMIN_DRAWER_EXIT_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
          transformOrigin: isRightDrawer
            ? "right center"
            : isDesktop || useCenteredMobile
              ? "center center"
              : "center bottom",
          boxShadow: isOperationalSheet
            ? "var(--dashboard-elevation-operational)"
            : "var(--dashboard-elevation-overlay)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
            flex: "0 0 auto",
            minWidth: 0,
            paddingBottom:
              isOperationalSheet || isDashboard ? "0.85rem" : undefined,
            borderBottom:
              isOperationalSheet || isDashboard
                ? "1px solid var(--login-border)"
                : undefined,
          }}
        >
          <h2
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: "1.25rem",
              letterSpacing: "0.04em",
              color: "var(--login-text)",
              margin: 0,
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </h2>
          <button
            aria-label="Fechar"
            onClick={requestClose}
            disabled={closeDisabled}
            className="admin-drawer-close"
            style={{
              background: "none",
              border: isOperationalSheet
                ? "1px solid var(--login-border)"
                : "none",
              cursor: "pointer",
              color: "var(--login-muted)",
              width:
                isOperationalSheet || isContextualSurface || isDashboard
                  ? "2.75rem"
                  : undefined,
              height:
                isOperationalSheet || isContextualSurface || isDashboard
                  ? "2.75rem"
                  : undefined,
              minWidth: isContextualSurface || isDashboard ? "44px" : undefined,
              minHeight:
                isContextualSurface || isDashboard ? "44px" : undefined,
              padding:
                isOperationalSheet || isContextualSurface || isDashboard
                  ? 0
                  : "0.25rem",
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: isOperationalSheet ? "4px" : "9999px",
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>
        {hasManagedContent ? (
          <div
            style={{
              display: "flex",
              flex: "1 1 auto",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </div>,
    document.body,
  );
}
